/**
 * dataLayer.js — Central GA4 ecommerce tracking via GTM dataLayer.
 *
 * Exports event functions + init helpers for use across JS modules and Twig templates.
 * Exposed on window.__avariDataLayer for inline / Alpine access.
 */

function push(event, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push({ event, ...data });
}

function getTrackingTax(flow) {
    return Number(flow.quote?.tax ?? flow.quote?.taxAmount ?? flow.quote?.taxes ?? 0);
}

function getTrackingNights(flow) {
    return Number(flow.quote?.nightsCount ?? 1) || 1;
}

function getTrackingValue(flow) {
    return Number(flow.quote?.total ?? 0);
}

function getTrackingItemPrice(flow) {
    const nightsCount = getTrackingNights(flow);
    const nightlyRate = Number(flow.nightlyRate ?? NaN);

    if (Number.isFinite(nightlyRate) && nightlyRate > 0) {
        return nightlyRate;
    }

    const basePrice = Number(flow.quote?.basePrice ?? flow.quote?.money?.fareAccommodation ?? NaN);
    if (Number.isFinite(basePrice) && nightsCount > 0) {
        return basePrice / nightsCount;
    }

    const total = getTrackingValue(flow);
    const tax = getTrackingTax(flow);
    return nightsCount > 0 ? Math.max(0, total - tax) / nightsCount : total;
}

function buildTrackingItem(flow) {
    return {
        item_id: flow.listingId || '',
        item_name: flow.itemName || '',
        item_category: flow.itemCategory || '',
        price: getTrackingItemPrice(flow),
        quantity: getTrackingNights(flow),
    };
}

// ─── Event functions ───

/**
 * search_initiated — fired when user submits a new search on the search page.
 */
export function searchInitiated(store) {
    const [checkIn, checkOut] = store.parseDates();
    push('search_initiated', {
        search_destination: store.where,
        search_check_in: checkIn,
        search_check_out: checkOut,
        search_nights: store.nights,
        search_guests: store.guests,
        search_adults: store.adults,
        search_children: store.children,
        search_pets: store.pets,
    });
}

/**
 * view_item_list — fired when search results are rendered.
 */
export function viewItemList(store) {
    const items = (store.results.listings || []).map((listing, index) => {
        const id = listing.guestyListingId || listing.id;
        return {
            item_id: id,
            item_name: listing.title || listing.unitName || '',
            item_category: store.where || '',
            index: index,
            price: store.getListingPrice(id) || listing.basePrice || undefined,
        };
    });

    push('view_item_list', {
        ecommerce: {
            item_list_name: 'search_results',
            items,
        },
    });
}

/**
 * select_item — fired when a user clicks a property card.
 */
export function selectItem(itemData, listName) {
    push('select_item', {
        ecommerce: {
            item_list_name: listName || '',
            items: [{
                item_id: itemData.item_id || '',
                item_name: itemData.item_name || '',
                item_category: itemData.item_category || '',
                index: itemData.index != null ? Number(itemData.index) : undefined,
                price: itemData.price != null ? Number(itemData.price) : undefined,
            }],
        },
    });
}

/**
 * view_item — fired on listing detail pages.
 * Reads static data from a #listing-view-data JSON block + URL params for nights.
 */
export function viewItem(data) {
    const params = new URLSearchParams(window.location.search);
    const dates = params.get('dates');
    let nights = null;

    if (dates && dates.includes(' to ')) {
        const [checkIn, checkOut] = dates.split(' to ').map(d => d.trim());
        const start = new Date(checkIn + 'T00:00:00');
        const end = new Date(checkOut + 'T00:00:00');
        if (!isNaN(start) && !isNaN(end)) {
            nights = Math.round((end - start) / 86400000);
        }
    }

    const item = {
        item_id: data.item_id || '',
        item_name: data.item_name || '',
        item_category: data.item_category || '',
        price: data.base_price != null ? Number(data.base_price) : undefined,
    };

    if (nights) {
        item.quantity = nights;
    }

    push('view_item', {
        ecommerce: {
            items: [item],
        },
    });
}

/**
 * begin_checkout — fired after quote review succeeds on checkout.
 */
export function beginCheckout(flow) {
    const total = getTrackingValue(flow);
    const tax = getTrackingTax(flow);

    push('begin_checkout', {
        ecommerce: {
            value: total,
            tax,
            currency: flow.currency || 'USD',
            items: [buildTrackingItem(flow)],
        },
        check_in_date: flow.checkIn || '',
        check_out_date: flow.checkOut || '',
        guest_count: (flow.adults || 0) + (flow.children || 0),
        is_authenticated: !!flow.currentUser,
    });
}

/**
 * update_item_dates — fired on listing pages after a date refresh updates quote details.
 */
export function updateItemDates(flow) {
    push('update_item_dates', {
        check_in_date: flow.checkIn || '',
        check_out_date: flow.checkOut || '',
        guest_count: (flow.adults || 0) + (flow.children || 0),
        ecommerce: {
            currency: flow.currency || 'USD',
            value: getTrackingValue(flow),
            items: [buildTrackingItem(flow)],
        },
    });
}

export function trackReviewEvent(flow) {
    if (flow.trackingContext === 'checkout' || window.location.pathname.startsWith('/checkout')) {
        beginCheckout(flow);
        return;
    }

    updateItemDates(flow);
}

/**
 * login / sign_up — fired on HTMX auth events.
 */
export function authEvent(eventName, method) {
    const isCheckout = window.location.pathname.startsWith('/checkout');
    push(eventName, {
        method: method || 'email',
        checkout_context: isCheckout ? 'checkout' : 'site',
    });
}

/**
 * add_payment_info — fired after payment tokenization or saved method selection.
 */
export function addPaymentInfo(flow, paymentType) {
    const total = flow.quote?.total || 0;
    const tax = flow.quote?.tax || flow.quote?.taxAmount || 0;

    push('add_payment_info', {
        ecommerce: {
            value: total,
            tax: tax,
            currency: flow.currency || 'USD',
            payment_type: paymentType || 'credit_card',
        },
    });
}

/**
 * purchase — called from confirmation page inline script.
 * Dedup handled by the caller via sessionStorage.
 */
export function purchase(data) {
    push('purchase', {
        ecommerce: {
            transaction_id: data.transaction_id || '',
            value: data.value || 0,
            tax: data.tax || 0,
            currency: data.currency || 'USD',
            items: data.items || [],
        },
        check_in_date: data.check_in_date || '',
        check_out_date: data.check_out_date || '',
        guest_count: data.guest_count || 0,
    });
}

// ─── Init helpers ───

/**
 * Listen for HTMX login/signup events and push auth dataLayer events.
 */
export function initAuthListeners() {
    if (typeof htmx === 'undefined') return;

    htmx.on('loginSucceeded', () => {
        authEvent('login', 'email');
    });
    htmx.on('signupSucceeded', () => {
        authEvent('sign_up', 'email');
    });
}

/**
 * Event delegation for [data-track-select-item] clicks on destination pages.
 * Only fires when the card is inside a [data-item-list-name] ancestor.
 */
export function initCardClickTracking() {
    document.addEventListener('click', (e) => {
        const card = e.target.closest('[data-track-select-item]');
        if (!card) return;

        const listContainer = card.closest('[data-item-list-name]');
        const listName = listContainer?.dataset.itemListName || 'destination';

        selectItem({
            item_id: card.dataset.itemId || '',
            item_name: card.dataset.itemName || '',
            item_category: card.dataset.itemCategory || '',
            index: card.dataset.itemIndex != null ? Number(card.dataset.itemIndex) : undefined,
            price: card.dataset.itemPrice ? Number(card.dataset.itemPrice) : undefined,
        }, listName);
    });
}

/**
 * Read #listing-view-data JSON and push view_item on listing pages.
 */
export function initViewItem() {
    const el = document.getElementById('listing-view-data');
    if (!el) return;

    try {
        const data = JSON.parse(el.textContent);
        viewItem(data);
    } catch (e) {
        // Silently ignore malformed JSON
    }
}

// ─── Expose on window for Twig / Alpine inline access ───

window.__avariDataLayer = {
    searchInitiated,
    viewItemList,
    selectItem,
    viewItem,
    beginCheckout,
    updateItemDates,
    trackReviewEvent,
    authEvent,
    addPaymentInfo,
    purchase,
    initAuthListeners,
    initCardClickTracking,
    initViewItem,
};
